// #618 (ADR-069) — the over-limit treasurer flag's one-click "cancel this donor's
// recurring gift". Given the over-limit contribution id, the server follows the
// series-link on the Contribution to the exact subscription and cancels just that
// one series (mirror → Canceled, reason limit-reached). No-ops cleanly for a
// one-time gift. On success we refresh the dashboard attention panel (the row's
// button state) and the recurring summary.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CancelOverLimitRecurringRequest, CancelOverLimitRecurringResponse } from '@signalvote/shared';
import { apiJson } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';

export type { CancelOverLimitRecurringResponse };

/**
 * Cancels the recurring series behind an over-limit contribution. The mutation
 * variable is the over-limit `contributionId`.
 */
export function useCancelOverLimitRecurring() {
  const queryClient = useQueryClient();
  return useMutation<CancelOverLimitRecurringResponse, Error, string>({
    mutationFn: (contributionId: string) =>
      apiJson<CancelOverLimitRecurringResponse>('/api/finance/recurring/cancel-over-limit', {
        method: 'POST',
        json: { contributionId } satisfies CancelOverLimitRecurringRequest,
        error: 'Failed to cancel the recurring gift',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.dashboard.attention() });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.recurring() });
    },
  });
}
